import React, { memo, useRef, useLayoutEffect, useState, useMemo, useCallback } from "react";
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { currentProjectAtom } from "~/stores/ProjectStore";
import { CommentMapUtilOptimized } from "./CommentMapUtilOptimized";
import { showCommentOnMapAtom } from "./CommentStore";
import { useEffect } from "react";
import { CommentAPIs } from "~/common/typescript/apis/CommentAPIs";
import { Comment_GetPage_RequestQuery } from "~/common/typescript/apis/CommentAPIs";
import CommentItem, { CommentItemMode } from "../panels/CommentPanel/CommentItem/CommentItem";
import maplibregl from "maplibre-gl";
import { LngLat, LngLatBounds } from "maplibre-gl";
import { Comment_Base } from "~/common/typescript/models/Comment";
import styled from "styled-components";
import { FaXmark } from "react-icons/fa6";
import { commentsAtom, commentDisplayedBoundsAtom, newlyCreatedCommentIdAtom, showCommentLabelsAtom } from "./CommentStore";
import { isMapLoadedDoneAtom } from "../stores/mapStore";
import { allowAddCommentOnMapAtom } from "./CommentStore";
import * as Turf from '@turf/turf';
import ReactDOM from 'react-dom';
import { createPortal } from 'react-dom';

// Track click timeouts to prevent double click zoom
let clickTimeouts = new Map<number, NodeJS.Timeout>();

const CommentMapController = () => {
  const [allowAddCommentOnMap, setAllowAddCommentOnMap] = useAtom(allowAddCommentOnMapAtom);
  const [showCommentOnMap, setshowCommentOnMap] = useAtom(showCommentOnMapAtom);
  const [currentProject, setCurrentProject] = useAtom(currentProjectAtom);
  const [comments, setComments] = useAtom(commentsAtom);
  const popupRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [isMapLoadedDone, setMapLoadedDone] = useAtom(isMapLoadedDoneAtom);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activePopups, setActivePopups] = useState<Set<number>>(new Set());
  const setCommentDisplayedBounds = useSetAtom(commentDisplayedBoundsAtom);
  const [newlyCreatedCommentId, setNewlyCreatedCommentId] = useAtom(newlyCreatedCommentIdAtom);
  const showCommentLabels = useAtomValue(showCommentLabelsAtom);

  // Filter out deleted comments (same logic as CommentPanel) - memoized for performance
  const filteredComments = useMemo(() =>
    comments.filter((comment) => !comment.is_deleted),
    [comments]
  );

  // Cleanup popups for deleted comments
  useEffect(() => {
    const currentCommentIds = new Set(filteredComments.map(c => c.id));

    // Only cleanup popups that are in activePopups but not in filteredComments
    setActivePopups(prevActivePopups => {
      const newActivePopups = new Set<number>();

      prevActivePopups.forEach(commentId => {
        if (currentCommentIds.has(commentId)) {
          // Comment still exists, keep popup
          newActivePopups.add(commentId);
        } else {
          // Comment was deleted, remove popup
          const popup = CommentMapUtilOptimized.hashPopupByCommentId.get(commentId);
          if (popup) {
            try {
              popup.remove();
            } catch (error) {
              console.warn('Error removing popup for deleted comment:', commentId, error);
            }
            CommentMapUtilOptimized.hashPopupByCommentId.delete(commentId);
          }
          popupRefs.current.delete(commentId);
        }
      });

      return newActivePopups;
    });
  }, [filteredComments]);

  useEffect(() => {
    if (!showCommentOnMap) {
      CommentMapUtilOptimized.removeCommentsFromMap(filteredComments);

      if (CommentMapUtilOptimized.hashPopupByCommentId) {
        CommentMapUtilOptimized.hashPopupByCommentId.forEach((popup, commentId) => {
          if (popup && typeof popup.remove === 'function') {
            popup.remove();
          }
        });
      }
    }
  }, [showCommentOnMap, filteredComments]);

  useEffect(() => {
    if (!isMapLoadedDone) return;
    if (showCommentOnMap) {
      CommentMapUtilOptimized.updateComments(filteredComments, showCommentLabels);

      let combinedCommentBounds: LngLatBounds | null = null;
      let commentsWithGeojson = 0;
      let commentsWithValidBounds = 0;

      filteredComments.forEach(comment => {
        if (comment.geojson) {
          commentsWithGeojson++;
          try {
            const geojsonObj = JSON.parse(comment.geojson);
            const bbox = Turf.bbox(geojsonObj);
            const commentBounds = new LngLatBounds([bbox[0], bbox[1]], [bbox[2], bbox[3]]);
            if (combinedCommentBounds) {
              combinedCommentBounds.extend(commentBounds);
            } else {
              combinedCommentBounds = commentBounds;
            }
            commentsWithValidBounds++;
          } catch (error) {
            console.error("Error parsing geojson for comment:", comment.id, error);
          }
        }
      });


      if (combinedCommentBounds) {
        const boundsString = JSON.stringify([
          [combinedCommentBounds.getWest(), combinedCommentBounds.getSouth()],
          [combinedCommentBounds.getEast(), combinedCommentBounds.getNorth()],
        ]);
        setCommentDisplayedBounds(boundsString);
      } else {
        setCommentDisplayedBounds(null);
      }

      const _comments = filteredComments;
      const _createPopup = (commentId: number) => {
        const popup = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          anchor: 'top-left',
          offset: [25, -30],
          maxWidth: '500px',
          closeOnMove: false,
        });
        CommentMapUtilOptimized.hashPopupByCommentId.set(commentId, popup);

        const comment: Comment_Base = _comments.find((comment) => comment.id === commentId) as Comment_Base;
        if (!comment) return;

        // Create popup container
        const popupContainer = document.createElement('div');
        popupContainer.id = `popup-container-${commentId}`;

        // Store reference for React portal first
        popupRefs.current.set(commentId, popupContainer);

        // Add to active popups first to trigger React render
        setActivePopups(prev => new Set([...prev, commentId]));

        // Use requestAnimationFrame for better performance than setTimeout
        requestAnimationFrame(() => {
          try {
            popup
              .setLngLat(
                new LngLat(JSON.parse(comment.geojson!).coordinates[0], JSON.parse(comment.geojson!).coordinates[1]),
              )
              .setDOMContent(popupContainer)
              .addTo(window.map!);
          } catch (error) {
            console.warn('Error creating popup for comment:', commentId, error);
            // Clean up on error
            setActivePopups(prev => {
              const newSet = new Set(prev);
              newSet.delete(commentId);
              return newSet;
            });
            popupRefs.current.delete(commentId);
          }
        });
      }
      const onClick = (e: any) => {
        const item = e.features?.[0];
        if (!item) return;

        const commentId = item.properties?.id;
        if (!commentId) return;

        // Clear any existing timeout for this comment to prevent double click
        if (clickTimeouts.has(commentId)) {
          clearTimeout(clickTimeouts.get(commentId)!);
          clickTimeouts.delete(commentId);
        }

        // Set a timeout to handle the click after double click detection window
        const timeout = setTimeout(() => {
          clickTimeouts.delete(commentId);

          if (CommentMapUtilOptimized.hashPopupByCommentId.has(commentId)) {
            const popup = CommentMapUtilOptimized.hashPopupByCommentId.get(commentId);
            if (popup) {
              popup.remove();
              CommentMapUtilOptimized.hashPopupByCommentId.delete(commentId);
              setActivePopups(prev => {
                const newSet = new Set(prev);
                newSet.delete(commentId);
                return newSet;
              });
              popupRefs.current.delete(commentId);
            }
            return;
          }

          _createPopup(commentId);
        }, 250); // 250ms delay to detect double click

        clickTimeouts.set(commentId, timeout);
      }

      // Prevent double click zoom on comment avatars
      const onDoubleClick = (e: any) => {
        const item = e.features?.[0];
        if (item) {
          const commentId = item.properties?.id;
          if (commentId) {
            // Clear the pending single click timeout since this is a double click
            if (clickTimeouts.has(commentId)) {
              clearTimeout(clickTimeouts.get(commentId)!);
              clickTimeouts.delete(commentId);
            }
          }

          // Prevent map zoom on double click when clicking comment avatar
          e.preventDefault();
        }
      };
      {
        CommentMapUtilOptimized.hashPopupByCommentId.forEach((popup, commentId) => {
          if (popup) {
            if (popup && typeof popup.remove === 'function') {
              popup.remove();
            }
          }
          _createPopup(commentId);
        });

        // Add click handler for the optimized layer
        window.map?.on('click', CommentMapUtilOptimized._layerId, onClick);
        // Add double click handler to prevent zoom
        window.map?.on('dblclick', CommentMapUtilOptimized._layerId, onDoubleClick);
      }

      return () => {
        // Remove click handler for the optimized layer
        window.map?.off('click', CommentMapUtilOptimized._layerId, onClick);
        // Remove double click handler for the optimized layer
        window.map?.off('dblclick', CommentMapUtilOptimized._layerId, onDoubleClick);
        // Clear any pending click timeouts
        clickTimeouts.forEach((timeout) => clearTimeout(timeout));
        clickTimeouts.clear();
      }
    }
  }, [isMapLoadedDone, filteredComments, showCommentOnMap, showCommentLabels]);

  const fetchData = () => {
    const payload: Comment_GetPage_RequestQuery = {
      projectId: currentProject.id as number,
      page: 1,
      pageSize: 100,
    }
    CommentAPIs.remote_getPage(payload).then((res) => {
      setComments(res.results);
    })
  }

  useEffect(() => {
    if (!currentProject) return;
    if (!isMapLoadedDone) return;

    fetchData();
  }, [isMapLoadedDone, currentProject]);

  // Handle newly created comment - auto show popup in edit mode
  useEffect(() => {
    if (!newlyCreatedCommentId || !showCommentOnMap) return;

    // Wait a bit for the comment to be rendered on map
    const timer = setTimeout(() => {
      const comment = comments.find(c => c.id === newlyCreatedCommentId);
      if (comment) {
        // Use the same _createPopup logic for newly created comments
        const popup = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          anchor: 'top-left',
          offset: [25, -30],
          maxWidth: '500px',
          closeOnMove: false,
        });

        CommentMapUtilOptimized.hashPopupByCommentId.set(newlyCreatedCommentId, popup);

        // Create popup container
        const popupContainer = document.createElement('div');
        popupContainer.id = `popup-container-${newlyCreatedCommentId}`;

        // Store reference and add to active popups first
        popupRefs.current.set(newlyCreatedCommentId, popupContainer);
        setActivePopups(prev => new Set([...prev, newlyCreatedCommentId]));

        // Use requestAnimationFrame for better performance
        requestAnimationFrame(() => {
          try {
            popup
              .setLngLat(
                new LngLat(JSON.parse(comment.geojson!).coordinates[0], JSON.parse(comment.geojson!).coordinates[1]),
              )
              .setDOMContent(popupContainer)
              .addTo(window.map!);
          } catch (error) {
            console.warn('Error creating popup for newly created comment:', newlyCreatedCommentId, error);
            // Clean up on error
            setActivePopups(prev => {
              const newSet = new Set(prev);
              newSet.delete(newlyCreatedCommentId);
              return newSet;
            });
            popupRefs.current.delete(newlyCreatedCommentId);
          }
        });

        // Clear the newly created comment ID after a delay to ensure portal renders
        setTimeout(() => {
          setNewlyCreatedCommentId(null);
        }, 500);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [newlyCreatedCommentId, filteredComments, showCommentOnMap, setNewlyCreatedCommentId]);


  // Memoize popup close handler to prevent re-renders
  const handlePopupClose = useCallback((commentId: number) => {
    const popup = CommentMapUtilOptimized.hashPopupByCommentId.get(commentId);
    if (popup && typeof popup.remove === 'function') {
      popup.remove();
    }
    CommentMapUtilOptimized.hashPopupByCommentId.delete(commentId);
    setActivePopups(prev => {
      const newSet = new Set(prev);
      newSet.delete(commentId);
      return newSet;
    });
    popupRefs.current.delete(commentId);
  }, []);

  // Memoize portal elements to reduce re-renders
  const portalElements = useMemo(() => {
    return Array.from(activePopups).map(commentId => {
      const comment = filteredComments.find(c => c.id === commentId);
      const container = popupRefs.current.get(commentId);

      if (!comment || !container) return null;

      return createPortal(
        <UI_PopupWrapper
          key={commentId}
          onClick={(e) => {
            // Only close popup if clicking the close button
            const isCloseButton = e.target instanceof Element && e.target.closest('.popup-close-button');

            if (isCloseButton) {
              handlePopupClose(commentId);
            }

            // Stop propagation to prevent map click handler from triggering
            e.stopPropagation();
          }}
        >
          <UI_PopupCloseButton className="popup-close-button">
            <FaXmark />
          </UI_PopupCloseButton>
          <div style={{ width: 250 }} onClick={(e) => e.stopPropagation()}>
            <CommentItem
              comment={comment}
              mode={comment.id === newlyCreatedCommentId ? CommentItemMode.Edit : CommentItemMode.View}
            />
          </div>
        </UI_PopupWrapper>,
        container,
        `popup-${commentId}`
      );
    });
  }, [activePopups, filteredComments, newlyCreatedCommentId, handlePopupClose]);

  // Render CommentItems using React Portal into popup containers
  return (
    <div ref={containerRef} style={{ display: 'none' }}>
      {portalElements}
    </div>
  );
};

export default memo(CommentMapController);

const UI_PopupWrapper = styled.div`
  width: 250px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  overflow: hidden;
  position: relative;
`;

const UI_PopupCloseButton = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  cursor: pointer;
  font-size: 18px;
  color: #FF000099;
  padding: 4px;
  z-index: 1000;
  &:hover {
    background-color: #e1e1e1;
    border-radius: 4px;
  }
`
